import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Backpack, Briefcase, Check, ShoppingBag, Users } from "lucide-react";
import AvailabilityCalendar from "./AvailabilityCalendar";
import PriceSelector from "./PriceSelector";
import { post } from "../../utils/request";
import { notifyCartUpdated } from "../Cart/CartContext";

const validOptions = (sku) => (sku?.options || []).filter((option) => (
  String(option?.name || "").trim()
  && Number.isFinite(Number(option?.sellingPrice))
));

const PackageSelector = ({
  listing,
  selectedPackage,
  selectedSku,
  selectedSlot,
  setSelectedSlot,
  onPackageChange,
  onSkuChange,
  onPreSettlementResultChange,
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [preSettlementResult, setPreSettlementResult] = useState(null);
  const [submitting, setSubmitting] = useState("");
  const availabilityRequest = useRef(0);

  const packageGroups = useMemo(() => (listing?.packages || [])
    .filter((group) => Array.isArray(group?.skus) && group.skus.length > 0), [listing]);
  const packageOptions = selectedPackage?.skus || [];

  useEffect(() => {
    const nextItems = validOptions(selectedSku).map((option) => ({
      name: option.name,
      sellingPrice: Number(option.sellingPrice),
      originalPrice: Number(option.originalPrice ?? option.sellingPrice),
      maxQty: Number.isFinite(Number(option.maxQty)) ? Number(option.maxQty) : 99,
      quantity: 0,
    }));
    setItems(nextItems);
    setPreSettlementResult(null);
    onPreSettlementResultChange?.(null);
  }, [selectedSku, onPreSettlementResultChange]);

  useEffect(() => {
    if (!selectedSku || selectedSku.ticketType !== "Scheduled") {
      setSlots([]);
      return undefined;
    }
    setLoadingSlots(true);
    setSlots(Array.isArray(selectedSku.availableSlots) ? selectedSku.availableSlots : []);
    setLoadingSlots(false);
    return undefined;
  }, [selectedSku]);

  const refreshAvailability = useCallback(async (month) => {
    if (!selectedSku || selectedSku.ticketType !== "Scheduled") return;
    const requestedMonth = month instanceof Date && !Number.isNaN(month.getTime())
      ? month
      : new Date();
    const dateFrom = [
      requestedMonth.getFullYear(),
      String(requestedMonth.getMonth() + 1).padStart(2, "0"),
      "01",
    ].join("-");
    const requestNumber = availabilityRequest.current + 1;
    availabilityRequest.current = requestNumber;
    setLoadingSlots(true);
    try {
      const result = await post(`/thingsToDo/product/${listing.id}/availability/verify`, {
        packageId: selectedSku.id,
        dateFrom,
        days: 31,
        currency: listing.currency,
      });
      if (availabilityRequest.current !== requestNumber) return;
      const refreshedSku = (result?.product?.packages || [])
        .flatMap((group) => group?.skus || [])
        .find((sku) => Number(sku.id) === Number(selectedSku.id));
      if (refreshedSku) setSlots(Array.isArray(refreshedSku.availableSlots) ? refreshedSku.availableSlots : []);
      if (!['completed', 'empty', 'superseded'].includes(result?.status)) {
        toast.info("Showing recently cached availability. Live refresh is still unavailable.");
      }
    } catch {
      if (availabilityRequest.current === requestNumber) {
        toast.info("Showing recently cached availability. Live refresh is still unavailable.");
      }
    } finally {
      if (availabilityRequest.current === requestNumber) setLoadingSlots(false);
    }
  }, [listing.currency, listing.id, selectedSku]);

  useEffect(() => {
    if (!selectedSku || selectedSku.ticketType !== "Scheduled") return undefined;
    void refreshAvailability(new Date());
    return undefined;
  }, [selectedSku, refreshAvailability]);

  const selectPackageOption = (sku) => {
    if (sku.id === selectedSku?.id) return;
    setSelectedSlot(null);
    onSkuChange(sku);
  };

  const updateQuantity = (name, quantity) => {
    setItems((current) => {
      const next = current.map((item) => item.name === name ? { ...item, quantity } : item);
      const selections = next.filter((item) => item.quantity > 0);
      if (selections.length) {
        post("/thingsToDo/preSettlement", {
          packageId: selectedSku.id,
          items: next.map((item) => ({ name: item.name, quantity: item.quantity })),
        }).then((result) => {
          setPreSettlementResult(result);
          onPreSettlementResultChange?.(result);
        }).catch(() => {
          setPreSettlementResult(null);
          onPreSettlementResultChange?.(null);
        });
      } else {
        setPreSettlementResult(null);
        onPreSettlementResultChange?.(null);
      }
      return next;
    });
  };

  const hasQuantity = items.some((item) => item.quantity > 0);
  const requiresDate = selectedSku?.ticketType === "Scheduled";
  const canContinue = Boolean(selectedSku && hasQuantity && (!requiresDate || selectedSlot));

  const createPreorder = () => post("/thingsToDo/preorder", {
    productId: selectedSku.productId,
    packageId: selectedSku.id,
    slotKey: selectedSlot?.slotKey,
    items: items.map((item) => ({ name: item.name, quantity: item.quantity })),
  });

  const continueBooking = async (destination) => {
    if (!canContinue || submitting) return;
    setSubmitting(destination);
    try {
      const preorder = await createPreorder();
      const preorderId = preorder?.preorderInfo?.preorderId;
      if (!preorderId) throw new Error("The selected package could not be reserved.");
      if (destination === "checkout") {
        navigate("/things-to-do/checkout", { state: { listing, thingsToDoPreorderResult: preorder } });
        return;
      }
      await post("/cart/addItem", {
        serviceType: "thingsToDo",
        item: { preorderId },
        source: "things_to_do_detail",
      });
      notifyCartUpdated();
      toast.success("Experience added to your cart.");
      navigate("/cart");
    } catch (error) {
      toast.error(error?.message || "Unable to reserve this package.");
    } finally {
      setSubmitting("");
    }
  };

  if (!packageGroups.length) {
    return <section className="things-section-card"><div className="things-results-status">No bookable package options are currently available.</div></section>;
  }

  return (
    <section className="things-section-card things-package-picker" id="package-options">
      {packageGroups.length > 1 && <div className="things-package-groups">
        <span>Package category</span>
        <div>{packageGroups.map((group, index) => {
          const label = String(group.name || "").trim() || `Package ${index + 1}`;
          return <button type="button" key={`${label}-${index}`} className={group === selectedPackage ? "is-selected" : ""} onClick={() => onPackageChange(group.name, group)}>{label}</button>;
        })}</div>
      </div>}

      <div className="things-package-options">
        <span>Package option</span>
        <div>{packageOptions.map((sku) => {
          const selected = sku.id === selectedSku?.id;
          const prices = validOptions(sku).map((option) => Number(option.sellingPrice));
          const fromPrice = prices.length ? Math.min(...prices) : null;
          return <button type="button" key={sku.id} className={selected ? "is-selected" : ""} onClick={() => selectPackageOption(sku)} disabled={sku.availableCapacity === 0}>
            <span><strong>{sku.title || "Standard package"}</strong><small>{sku.ticketType === "Open Dated" ? "Open-dated access" : "Choose an available date"}</small></span>
            <span className="things-package-option__meta">
              {sku.paxCount > 0 && <small><Users /> {sku.paxCount} passengers</small>}
              {sku.largeBags > 0 && <small><Briefcase /> {sku.largeBags} large bags</small>}
              {sku.smallBags > 0 && <small><Backpack /> {sku.smallBags} small bags</small>}
            </span>
            <span className="things-package-option__price">{fromPrice !== null ? `From ${listing.currency || "USD"} ${fromPrice.toFixed(2)}` : "Check price"}</span>
            {selected && <Check className="things-package-option__check" />}
          </button>;
        })}</div>
      </div>

      {selectedSku && <div className="things-selection-grid">
        {requiresDate && <div className="things-date-selection">
          <div><span>Visit date</span><strong>{selectedSlot?.slotDate || "Choose an available date"}</strong></div>
          {loadingSlots && <div className="things-results-status">Refreshing live availability…</div>}
          {slots.length ? <AvailabilityCalendar slots={slots} selected={selectedSlot} onSelect={setSelectedSlot} onMonthChange={refreshAvailability} /> : !loadingSlots && <div className="things-results-status">No selectable dates are currently available for this option.</div>}
        </div>}
        <div className="things-quantity-selection">
          <div><span>Travellers</span><strong>Select quantity</strong></div>
          {!items.length ? <p className="things-muted-text">No traveller prices are configured for this option.</p> : items.map((item) => <PriceSelector key={item.name} sku={selectedSku} selectedSlot={selectedSlot} option={item} onChange={(quantity) => updateQuantity(item.name, quantity)} currency={listing.currency} />)}
        </div>
      </div>}

      <div className="things-booking-summary">
        <div><span>Total</span><strong>{preSettlementResult?.displayTotalPrice || (hasQuantity ? "Confirming live price…" : "Select travellers")}</strong></div>
        <div>
          <button type="button" className="things-cart-action" disabled={!canContinue || Boolean(submitting)} onClick={() => continueBooking("cart")}><ShoppingBag /> {submitting === "cart" ? "Adding…" : "Add to cart"}</button>
          <button type="button" className="things-book-action" disabled={!canContinue || Boolean(submitting)} onClick={() => continueBooking("checkout")}>{submitting === "checkout" ? "Preparing…" : "Book now"}</button>
        </div>
      </div>
    </section>
  );
};

export default PackageSelector;
